import { useCookie, useNuxtApp, useRuntimeConfig } from 'nuxt/app';
import { defineStore } from 'pinia';
import { computed, readonly, ref, toValue } from 'vue';
import { useCurrentSession } from '~/composables/auth/useCurrentSession';
import type { AuthSession, User } from '~/types/auth/User';
import type { DocumentQuota } from '~/types/editor/Editor';
import type { ApiInstance, LaravelApiResponse } from '~/types/plugins/api';
import type { TemplateQuota } from '~/types/template/Template';
import {
  COMPANY,
  ENTERPRISE,
  INDIVIDUAL,
} from '~/utils/enums/subscription/SubscriptionProduct';

export const useAuthStore = defineStore('auth', () => {
  const { $apiClient } = useNuxtApp() as unknown as { $apiClient: ApiInstance };

  // State
  const session = ref<AuthSession | null>(null);
  const isLoading = ref(false);
  const isInitialized = ref(false);

  const { user: currentUser, status, token, getSession } = useCurrentSession();

  // Getters (computed)
  const user = computed((): User | null => session.value?.user ?? null);

  const username = computed((): string | null => {
    if (!isAuthenticated.value) return null;
    return user.value?.email || user.value?.phone_number || null;
  });

  const templateQuota = computed<TemplateQuota>(() => ({
    used: user.value?.organization?.template_count ?? 0,
    limit: user.value?.organization?.allowed_template_count ?? 0,
  }));

  const documentMonthlyQuota = computed<DocumentQuota>(() => {
    return {
      used: user.value?.organization?.documents_uploaded_this_month_count ?? 0,
      limit: user.value?.organization?.document_credits ?? 0,
    };
  });

  const documentMonthlyQuotaReached = computed(() => {
    const quota = documentMonthlyQuota.value;
    return quota.limit > 0 && quota.used >= quota.limit;
  });

  const isAuthenticated = computed((): boolean => {
    if (status.value === 'authenticated') return !!session.value?.user;
    if (status.value === 'loading')
      return !!session.value?.user || !!accessToken.value;
    return false;
  });

  const canChangePlan = computed((): boolean => {
    if (!isAuthenticated.value) return false;
    // Enterprise users cannot change their plan independently — they must
    // contact support — so no "Change Plan" button should be shown to them.
    if (hasEnterpriseSubscription.value) return false;
    return user.value?.organization?.role === 'OWNER';
  });

  const currentRole = computed(() => {
    const role = user.value?.organization?.role;
    if (role === 'OWNER') return 'Owner';
    if (role === 'USER_ADMIN') return 'Admin';
    if (role === 'USER') return 'Member';
    return 'User';
  });

  const isOwner = computed((): boolean => {
    if (!isAuthenticated.value) return false;
    return user.value?.organization?.role === 'OWNER';
  });

  // A decommissioned account (e.g. a member/admin whose company owner deleted
  // their account) is flagged via `blocked_at`/`deleted_at` on the user.
  const isDeactivated = computed((): boolean => {
    if (!isAuthenticated.value) return false;
    return !!user.value?.blocked_at || !!user.value?.deleted_at;
  });

  const hasAdminAccess = computed((): boolean => {
    if (!isAuthenticated.value) return false;
    const role = user.value?.organization?.role;
    return role === 'OWNER' || role === 'USER_ADMIN';
  });

  const hasTeamAccess = computed((): boolean => {
    if (!isAuthenticated.value) return false;
    if (!hasAdminAccess.value) return false;
    return !!user.value?.organization?.allow_sub_users_count;
  });

  const isVerified = computed((): boolean => {
    if (!isAuthenticated.value) return false;
    return (
      user.value?.email_verified || user.value?.phone_number_verified || false
    );
  });

  const isOnboarded = computed((): boolean => {
    if (!isAuthenticated.value) return false;
    return !!user.value?.onboarded_at;
  });

  // Owner: user has no organization yet (needs to create one during onboarding)
  const hasOrganization = computed((): boolean => {
    if (!isAuthenticated.value) return false;
    return !!user.value?.organization;
  });

  const accessToken = computed((): string | null => {
    // First try to get from auth module
    const authToken = token.value;
    if (authToken && authToken !== 'null') {
      return authToken;
    }

    // Read from cookie in both server and client contexts
    const accessCookie = createAccessCookie();
    const cookieValue = accessCookie.value;

    if (cookieValue && cookieValue !== 'null') {
      return cookieValue;
    }

    return null;
  });

  const subscription = computed(() => user.value?.subscription);

  const isSubscribed = computed((): boolean => {
    return !!subscription.value?.is_subscribed;
  });

  const isSubscriptionCancelled = computed<boolean>(() =>
    Boolean(subscription.value?.is_cancelled),
  );

  const hasIndividualSubscription = computed((): boolean => {
    return (
      subscription.value?.subscription_product?.toLowerCase() ===
      INDIVIDUAL.toLowerCase()
    );
  });

  const hasCompanySubscription = computed((): boolean => {
    return (
      subscription.value?.subscription_product?.toLowerCase() ===
      COMPANY.toLowerCase()
    );
  });

  const hasEnterpriseSubscription = computed((): boolean => {
    return (
      subscription.value?.subscription_product?.toLowerCase() ===
        ENTERPRISE.toLowerCase() || hasEnterpriseAccess.value
    );
  });

  const hasEnterpriseInquiry = computed((): boolean => {
    return !!subscription.value?.has_enterprise_inquiry;
  });

  const hasEnterpriseAccess = computed((): boolean => {
    return !!subscription.value?.has_enterprise_access;
  });

  const hasPendingEnterpriseInquiry = computed((): boolean => {
    return (
      hasEnterpriseInquiry.value &&
      !isOnboarded.value &&
      !hasEnterpriseAccess.value
    );
  });

  // Helper function to create access cookie
  const createAccessCookie = () => {
    const {
      token: { cookieName, maxAgeInSeconds },
    } = (useRuntimeConfig().public?.auth.provider as {
      token: { cookieName: string; maxAgeInSeconds: number };
    }) || { cookieName: 'auth:token', maxAgeInSeconds: 604800 };

    return useCookie<string | null>(cookieName as string, {
      default: () => null,
      secure: process.env.NODE_ENV === 'production',
      httpOnly: false,
      sameSite: 'lax',
      maxAge: maxAgeInSeconds,
    });
  };

  const setAccessToken = (newAccessToken: string): void => {
    const accessCookie = createAccessCookie();
    accessCookie.value = newAccessToken.replace(/^Bearer\s+/i, '');
  };

  // Actions
  /**
   * Initialize the auth store
   */
  const initialize = async (): Promise<void> => {
    if (isInitialized.value) return;

    isLoading.value = true;

    try {
      // Self-heal a token that was previously persisted with an
      // already-prefixed "Bearer <token>" value (see useAuthSession.signIn
      // historic bug). sidebase re-prefixes on every read, so a stale
      // double-prefixed token breaks /auth/me until stripped back to raw.
      const { rawToken } = useAuthState();
      if (rawToken.value && /^Bearer\s+/i.test(rawToken.value)) {
        rawToken.value = rawToken.value.replace(/^Bearer\s+/i, '');
      }

      const accessCookie = createAccessCookie();
      if (accessCookie.value && /^Bearer\s+/i.test(accessCookie.value)) {
        accessCookie.value = accessCookie.value.replace(/^Bearer\s+/i, '');
      }

      const currentUserData = currentUser.value as { data?: User } | undefined;
      const hasUserData =
        !!currentUserData?.data && Object.keys(currentUserData.data).length > 0;

      if (hasUserData) {
        setSession({ user: currentUserData.data as User } as AuthSession);
      } else if (accessCookie.value) {
        await refreshSession();
      } else {
        clearSession();
      }

      // One-time cleanup: delete leftover auth:data cookie from prior sessions
      const legacyCookieName =
        useRuntimeConfig().public?.appCookieName || 'auth:data';
      useCookie(legacyCookieName, { maxAge: 0 }).value = null;
    } catch (error) {
      console.error('Failed to initialize auth store:', error);
      session.value = null;
    } finally {
      isInitialized.value = true;
      isLoading.value = false;
    }
  };

  /**
   * Refresh session from AuthJS server and /auth/me API
   */
  const refreshSession = async (): Promise<AuthSession | null> => {
    isLoading.value = true;

    try {
      await getSession();
      if (currentUser.value) {
        const currentUserResponse = toValue(currentUser) as
          | { data?: User }
          | undefined;
        const userData = currentUserResponse?.data as User | undefined;

        // Ignore the `{ data: {} }` fallback returned by /auth/me on
        // transient 401/429 (see server/api/auth/me.get.ts) — an empty
        // object would otherwise overwrite a valid session and make
        // isVerified/isOnboarded incorrectly false.
        if (userData && Object.keys(userData).length > 0) {
          const sessionData = {
            user: { ...userData } as User,
          } as AuthSession;

          setSession(sessionData);

          return sessionData;
        }
      }

      return null;
    } catch (error) {
      console.error('Failed to refresh session:', error);

      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const syncUserData = async (): Promise<void> => {
    try {
      const user = await $apiClient.get<LaravelApiResponse<User>>('auth/me');
      setSession({
        ...session.value,
        user: user.data,
      });
    } catch (error) {
      console.error('Failed to sync session:', error);
    }
  };

  /**
   * Set session data
   */
  const setSession = (sessionData: AuthSession): void => {
    session.value = sessionData || null;
  };

  /**
   * Clear session data and cookie
   */
  const clearSession = (): void => {
    session.value = null;
    isInitialized.value = false;

    const config = useRuntimeConfig();

    // Delete access token cookie by setting maxAge to 0
    const {
      token: { cookieName },
    } = (config.public?.auth.provider as {
      token: { cookieName: string; maxAgeInSeconds: number };
    }) || { token: { cookieName: 'auth:token', maxAgeInSeconds: 604800 } };

    const accessCookie = useCookie(cookieName, {
      maxAge: 0,
      secure: process.env.NODE_ENV === 'production',
      httpOnly: false,
      sameSite: 'lax',
    });
    accessCookie.value = null;
  };
  // Return reactive state and actions
  return {
    // State
    session: readonly(session),
    isLoading: readonly(isLoading),
    isInitialized: readonly(isInitialized),

    // Getters
    isAuthenticated,
    user,
    username,
    isVerified,
    isOnboarded,
    hasOrganization,
    currentRole,
    isOwner,
    isDeactivated,
    hasAdminAccess,
    hasTeamAccess,
    accessToken,

    subscription,
    isSubscribed,
    isSubscriptionCancelled,
    hasIndividualSubscription,
    hasCompanySubscription,
    hasEnterpriseSubscription,
    hasEnterpriseInquiry,
    hasEnterpriseAccess,
    hasPendingEnterpriseInquiry,

    templateQuota,
    documentMonthlyQuota,
    documentMonthlyQuotaReached,
    canChangePlan,

    // Actions
    setAccessToken,
    initialize,
    refreshSession,
    syncUserData,
    setSession,
    clearSession,
  };
});
